import { useEffect, useState, useSyncExternalStore, type DragEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Lock, RefreshCw, Upload, X } from '../../../icons';
import {
  Button, EmptyState, FilterChip, FormField, Input, PageHeader, Select, Switch, Table, UnsavedGuard, toast,
  type TableColumn,
} from '../../../components/shared';
import { useAttachmentUpload } from '../../../components/Attachment/useAttachmentUpload';
import {
  createBoardPost, getBoard, getBoardPost, listBoards, notifyBoardPost, patchBoardPost, pinBoardPost,
  type BoardDto, type BoardPostDetail, type BoardPrefixDto,
} from '../../../api/boards';
import { formatBytes } from '../../../lib/attachments';
import { getBoardsLang, subscribeBoardsLang, tBoards } from '../../../lib/boards-i18n';
import { boardQuotaNoticeKey } from '../../BoardsPage/boardQuotaNotice';
import { summarizeTargets } from '../../BoardsPage/wide/BoardWriter';
import { BoardTargetModal } from '../../BoardsPage/wide/BoardTargetModal';
import { boardListPath, boardPostPath, boardSettingsPath, boardWritePath } from '../board-v1-url';
/**
 * 본문 편집기 — **지연 로드 문**으로만 부른다(`LazyRichTextEditor`). tiptap 은 글을 «쓰는»
 * 화면에서만 내려온다. `serializeForSave` 는 저장 직전에 쓰는 순수 함수라 정적으로 부른다.
 */
import { LazyRichTextEditor } from '../../../components/editor/LazyRichTextEditor';
import { serializeForSave } from '../../../components/editor/editor-html';
import { LazyAxiBoardEditor } from '../../../components/editor/axi/LazyAxiBoardEditor';
import { serializeAxiForSave } from '../../../components/editor/axi/axi-html';
import { BOARD_EDITOR } from '../../../components/editor/board-editor-choice';
import { saveBlockedByFailedImagesMessage, IMAGE_UPLOADING } from '../../../components/editor/editor-messages';
import {
  CONTENT_MAX, TITLE_MAX, buildCreatePayload, buildPatchPayload, canSubmitForm, changedFields, initialForm,
  type WriteAttachment, type WriteForm,
} from './write-model';
import './board-write.css';

/**
 * 글쓰기 · 수정 — 시안 v1 `axi-board-write` (U4 · 2026-09-15 · 주소 `/board/:boardId/write` · `/board/post/:postId/edit`).
 *
 *   머리(경로 · 제목 · 취소 · 등록/저장)
 *   [글 내용 카드: 올릴 게시판 · 말머리 · 제목 · 본문(서식 줄 자리) · 첨부 | 발행 설정 340: 옵션 행 · 알림 · 열람자 · 등록 막대]
 *
 * ★ 보내는 값은 현행 `BoardWriter` 와 같다(`write-model`) — 서버 규칙도 같다.
 * ★ 게시판 설정이 꺼진 줄은 **그리지 않는다**(규칙 1 익명 · 규칙 2 열람자). 알림이 꺼진 게시판은 잠긴 줄 + 안내(규칙 3).
 * ★ 수정은 알림을 자동으로 보내지 않는다 — 「알림 다시 보내기」를 눌렀을 때만(규칙 13). 고친 뒤 저장하지 않았으면 보내지 않는다.
 * ★★ 서버 짝이라 **그리지 않는 것**: 내용 확인 요청(R1) · 알림 제목 미리보기 · 알림 받을 인원 · 빠지는 사람(R5 · R6).
 *    알림 제목은 서버가 정한다(규칙 4) — 웹이 따로 조립하지 않고 「등록한 뒤 알려 드립니다」.
 * ★★ 열람자 설정이 꺼진 게시판의 기존 열람자 글(규칙 2 · Q-B): 읽기 전용 줄만 둔다. 「지정 해제」는 서버 PATCH 가
 *    그 게시판의 readers 를 무시하므로 지금은 누를 수 없는 단추가 된다 — 서버 요청 뒤(R20)에 붙인다.
 * ★ 서식 도구 줄은 편집기 결정 뒤(정본 제안 8) — 누를 수 없는 단추를 두지 않으려고 글자 자리만 그린다.
 */

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

type Loaded =
  | { status: 'loading' }
  | { status: 'ready'; board: BoardDto; prefixes: BoardPrefixDto[]; post: BoardPostDetail | null; boards: BoardDto[] }
  | { status: 'missing' }
  | { status: 'error'; message: string };

export type BoardWriteScreenProps = { mode: 'write'; boardId: string } | { mode: 'edit'; postId: string };

export function BoardWriteScreen(props: BoardWriteScreenProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const routeId = props.mode === 'write' ? props.boardId : props.postId;
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoaded({ status: 'loading' });
    const load = async (): Promise<Loaded> => {
      if (props.mode === 'write') {
        const [detail, list] = await Promise.all([
          getBoard(props.boardId),
          listBoards().catch(() => ({ boards: [] as BoardDto[] })),
        ]);
        return { status: 'ready', board: detail.board, prefixes: detail.prefixes, post: null, boards: list.boards };
      }
      const { post, board } = await getBoardPost(props.postId);
      const prefixes = await getBoard(board.id).then((d) => d.prefixes).catch(() => (post.prefix ? [post.prefix] : []));
      return { status: 'ready', board, prefixes, post, boards: [] };
    };
    load()
      .then((next) => { if (!cancelled) setLoaded(next); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as ApiError)?.response?.status;
        setLoaded(status === 404 || status === 403 ? { status: 'missing' } : { status: 'error', message: errMsg(e) });
      });
    return () => { cancelled = true; };
    // 주소가 바뀌면(다른 게시판 · 다른 글) 새로 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, routeId, attempt]);

  const heading = tBoards(props.mode === 'edit' ? 'boards.v1.screen.edit' : 'boards.v1.screen.write');
  const crumb = tBoards('boards.v1.crumb');
  const root = (children: ReactNode) => (
    <div className="bv1-page bv1-write" data-board-screen={props.mode} data-id={routeId}>
      {children}
    </div>
  );
  const plainHead = (boardName?: string) => (
    <PageHeader className="bv1-head" breadcrumb={[crumb, boardName ?? crumb, heading]} title={heading} />
  );

  if (loaded.status === 'loading') {
    return root(<>{plainHead()}<section className="bv1-card"><div className="bv1-loading" aria-busy="true">{tBoards('boards.v1.loading')}</div></section></>);
  }
  if (loaded.status === 'missing') {
    return root(
      <>
        {plainHead()}
        <section className="bv1-card">
          <EmptyState
            kind="no-permission"
            icon={Lock}
            title={props.mode === 'write' ? tBoards('boards.v1.write.board_missing') : tBoards('boards.v1.post.missing.title')}
            description={props.mode === 'write' ? undefined : tBoards('boards.v1.post.missing.desc')}
          />
        </section>
      </>,
    );
  }
  if (loaded.status === 'error') {
    return root(
      <>
        {plainHead()}
        <section className="bv1-card">
          <EmptyState
            kind="error"
            title={tBoards('boards.v1.load_failed')}
            description={loaded.message}
            action={<Button variant="secondary" size="compact" onClick={() => setAttempt((n) => n + 1)}>{tBoards('boards.v1.retry')}</Button>}
          />
        </section>
      </>,
    );
  }

  const { board, post } = loaded;
  const denied = post ? post.locked || post.canEdit === false : board.canWrite === false;
  if (denied) {
    return root(
      <>
        {plainHead(board.name)}
        <section className="bv1-card">
          <EmptyState
            kind="no-permission"
            icon={Lock}
            title={tBoards(post ? 'boards.v1.write.deny_edit.title' : 'boards.v1.write.deny_write.title')}
            description={tBoards(post ? 'boards.v1.write.deny_edit.desc' : 'boards.v1.write.deny_write.desc')}
          />
        </section>
      </>,
    );
  }

  return root(
    <WriteFormView
      key={post ? `edit-${post.id}` : `write-${board.id}`}
      board={board}
      prefixes={loaded.prefixes}
      post={post}
      boards={loaded.boards}
      heading={heading}
    />,
  );
}

function OptRow({ title, hint, off, children }: { title: string; hint?: ReactNode; off?: boolean; children?: ReactNode }) {
  return (
    <div className={`bv1-opt${off ? ' is-off' : ''}`}>
      <div className="bv1-opt__text">
        <b>{title}</b>
        {hint != null && <span>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

type FileRow =
  | { kind: 'done'; key: string; name: string; sizeBytes: number; fileId: string }
  | { kind: 'uploading'; key: string; name: string; percent: number }
  | { kind: 'failed'; key: string; name: string; sizeBytes: number; failedId: string; message: string };

interface WriteFormViewProps {
  board: BoardDto;
  prefixes: BoardPrefixDto[];
  post: BoardPostDetail | null;
  boards: BoardDto[];
  heading: string;
}

function WriteFormView({ board, prefixes, post, boards, heading }: WriteFormViewProps) {
  const navigate = useNavigate();
  const editing = !!post;
  const canManage = board.canManage === true;

  // 처음 값 — 「바뀐 항목」 · 버리기 창 판정의 기준. 이 화면이 사는 동안 바뀌지 않는다(key 로 새로 뜬다).
  const [startForm] = useState<WriteForm>(() => initialForm(board, post));
  const [startFiles] = useState(() => (post?.attachments ?? []).map((a) => ({
    fileId: a.fileId, fileKey: '', name: a.name, mimeType: a.mimeType ?? '', sizeBytes: a.sizeBytes ?? a.size ?? 0,
  })));
  const [form, setForm] = useState<WriteForm>(startForm);
  const set = <K extends keyof WriteForm>(key: K, value: WriteForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [renotifying, setRenotifying] = useState(false);
  const [picker, setPicker] = useState<'notify' | 'readers' | null>(null);
  const [dragOver, setDragOver] = useState(false);
  /*
   * ⛔ **「저장 길이 … / 50,000」을 여기서 또 그리지 않는다** (2026-09-23 운영에서 두 번 보였다).
   *   `RichTextEditor` 가 제 상자 안(`.axi-rte__length-count`)에 **같은 값을 이미** 그린다 —
   *   그쪽이 90%·초과 경고까지 함께 내는 정본이라, 이 화면이 `onLengthChange` 로 같은 값을 받아
   *   한 줄 더 적던 것이 «중복»이었다. 둘이 다른 것을 세던 것이 아니다:
   *   **한 곳(에디터의 length 상태)에서 나온 같은 수**였다.
   */
  // 등록 · 저장이 끝나 옮겨 갈 주소 — 버리기 창을 먼저 끈 뒤(다음 그리기) 옮긴다. 같은 틱에 옮기면 막힌다.
  const [leaveTo, setLeaveTo] = useState<string | null>(null);

  // 게시판 첨부의 용량은 그 게시판의 소유 스코프에서 나간다(현행과 같음) · 수정이면 붙어 있던 첨부를 씨앗으로.
  const upload = useAttachmentUpload({ onError: setError, boardId: board.id, initialAttachments: startFiles });
  const toWrite = (l: ReadonlyArray<{ fileId: string; name: string; sizeBytes: number; mimeType: string }>): WriteAttachment[] =>
    l.map((a) => ({ fileId: a.fileId, name: a.name, sizeBytes: a.sizeBytes, ...(a.mimeType ? { mimeType: a.mimeType } : {}) }));
  const files = toWrite(upload.attachments);
  const changed = changedFields(startForm, form, toWrite(startFiles), files);
  const dirty = changed.length > 0 || !!upload.uploadingFile;
  const canSubmit = canSubmitForm(form) && !submitting && !upload.uploadingFile && (!editing || changed.length > 0);

  useEffect(() => {
    if (leaveTo) navigate(leaveTo, { replace: true });
  }, [leaveTo, navigate]);

  const cancel = () => navigate(post ? boardPostPath(post.id) : boardListPath(board.id));

  async function pinAfter(postId: string, pinned: boolean) {
    try {
      await pinBoardPost(postId, pinned);
    } catch (e) {
      toast.error(tBoards('boards.v1.write.pin_err', { msg: errMsg(e) }));
    }
  }

  async function submit() {
    if (!canSubmitForm(form)) {
      setError(tBoards('boards.editor.err_required'));
      return;
    }
    /**
     * 저장할 꼴로 바꾼다 — presigned 주소를 만료 없는 주소로 갈고, 표 셀 정렬을 `align` 으로
     * 옮기고, 한 번 더 sanitize 한다(`serializeForSave`).
     *
     * ⛔ 그리고 **그림이 다 붙었는지 여기서 막는다.** 안 막으면
     *   ⓐ 올리는 중인 자리표시가 그대로 저장되고(그 그림은 «영영» 안 붙는다)
     *   ⓑ 올리지 못한 그림이 있는데 사용자는 «모르고» 저장한다.
     *   ★ 「사실대로 말한다」 — 몇 개를 잃을지 숫자로 알려 준다.
     */
    // 2026-10-14 — 편집기마다 저장 꼴이 다르다(AxiEditor 는 style·영상·도형을 남긴다). 깃발로 가른다.
    const saved = BOARD_EDITOR === 'axi' ? serializeAxiForSave(form.content) : serializeForSave(form.content);
    if (saved.uploadingCount > 0) {
      setError(IMAGE_UPLOADING);
      return;
    }
    if (saved.failedCount > 0) {
      setError(saveBlockedByFailedImagesMessage(saved.failedCount));
      return;
    }
    const formToSend: WriteForm = { ...form, content: saved.html };
    setSubmitting(true);
    setError(null);
    try {
      if (post) {
        await patchBoardPost(post.id, buildPatchPayload(formToSend, board, files));
        if (canManage && form.pinned !== startForm.pinned) await pinAfter(post.id, form.pinned);
        upload.clearAttachments();
        toast.success(tBoards('boards.v1.write.saved'));
        setLeaveTo(boardPostPath(post.id));
        return;
      }
      const { post: created, notified } = await createBoardPost(board.id, buildCreatePayload(formToSend, board, files));
      if (canManage && form.pinned) await pinAfter(created.id, true);
      upload.clearAttachments();
      toast.success(tBoards('boards.v1.write.created'));
      if (board.notifyEnabled && form.notify) toast.info(tBoards('boards.editor.notified', { n: notified }));
      setLeaveTo(boardPostPath(created.id));
    } catch (e) {
      setError(tBoards(post ? 'boards.editor.err_update' : 'boards.editor.err_create', { msg: errMsg(e) }));
    } finally {
      setSubmitting(false);
    }
  }

  async function renotify() {
    if (!post) return;
    setRenotifying(true);
    try {
      const n = await notifyBoardPost(post.id);
      toast.success(tBoards('boards.v1.write.renotify_done', { n }));
    } catch (e) {
      setError(tBoards('boards.v1.write.renotify_err', { msg: errMsg(e) }));
    } finally {
      setRenotifying(false);
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer?.files ?? []);
    if (dropped.length > 0) void upload.addFiles(dropped);
  };

  const fileRows: FileRow[] = [
    ...upload.attachments.map((a): FileRow => ({ kind: 'done', key: `f-${a.fileId}`, name: a.name, sizeBytes: a.sizeBytes, fileId: a.fileId })),
    ...(upload.uploadingFile ? [{ kind: 'uploading', key: 'uploading', name: upload.uploadingFile, percent: upload.uploadPercent } as FileRow] : []),
    ...upload.failed.map((f): FileRow => ({ kind: 'failed', key: `x-${f.id}`, name: f.name, sizeBytes: f.sizeBytes, failedId: f.id, message: f.message })),
  ];
  const atLimit = upload.attachments.length >= upload.maxItems;

  const fileColumns: TableColumn<FileRow>[] = [
    {
      key: 'name',
      header: tBoards('boards.v1.write.files.col_file'),
      render: (r) => <span className="bv1-file"><FileText aria-hidden className="bv1-file__icon" /><span className="bv1-file__name" title={r.name}>{r.name}</span></span>,
    },
    { key: 'size', header: tBoards('boards.v1.write.files.col_size'), numeric: true, width: '88px', render: (r) => (r.kind === 'uploading' ? '—' : formatBytes(r.sizeBytes)) },
    {
      key: 'state',
      header: tBoards('boards.v1.write.files.col_state'),
      width: '140px',
      render: (r) => {
        if (r.kind === 'done') return <span className="bv1-fstate is-done">{tBoards('boards.v1.write.files.done')}</span>;
        if (r.kind === 'uploading') {
          return (
            <span className="bv1-upbar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={r.percent} aria-label={r.name}>
              <span className="bv1-upbar__track"><i style={{ inlineSize: `${r.percent}%` }} /></span>
              <span className="bv1-num">{tBoards('boards.v1.write.files.uploading', { n: r.percent })}</span>
            </span>
          );
        }
        return (
          <span className="bv1-fstate is-failed" title={r.message}>
            {tBoards('boards.v1.write.files.failed')}
            <Button variant="ghost" size="sm" iconLeft={<RefreshCw aria-hidden />} onClick={() => void upload.retryFailed(r.failedId)}>
              {tBoards('boards.v1.write.files.retry')}
            </Button>
          </span>
        );
      },
    },
    {
      key: 'remove',
      header: <span className="bv1-sr">{tBoards('boards.v1.write.files.col_remove')}</span>,
      align: 'right',
      width: '56px',
      render: (r) => {
        if (r.kind === 'uploading') return null;
        const label = tBoards(r.kind === 'done' ? 'boards.v1.write.files.remove' : 'boards.v1.write.files.dismiss', { name: r.name });
        return (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={label}
            disabled={submitting}
            onClick={() => (r.kind === 'done' ? upload.handleRemoveAttachment(r.fileId) : upload.dismissFailed(r.failedId))}
          >
            <X aria-hidden />
          </Button>
        );
      },
    },
  ];

  const writableBoards = boards.filter((x) => x.canWrite !== false);
  const notifyOn = board.notifyEnabled && form.notify;
  const note = editing
    ? (changed.length > 0 ? tBoards('boards.v1.write.bar.changed', { n: changed.length }) : tBoards('boards.v1.write.bar.unchanged'))
    : tBoards(notifyOn ? 'boards.v1.write.bar.notify_on' : 'boards.v1.write.bar.notify_off');
  const submitLabel = tBoards(editing ? 'boards.btn.save' : 'boards.btn.register');

  return (
    <>
      <PageHeader
        className="bv1-head"
        breadcrumb={[tBoards('boards.v1.crumb'), board.name, heading]}
        title={heading}
        actions={(
          <>
            <Button variant="secondary" onClick={cancel} disabled={submitting}>{tBoards('boards.btn.cancel')}</Button>
            <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>{submitLabel}</Button>
          </>
        )}
      />

      <div className="bv1-write__layout">
        <section className="bv1-card bv1-write__card" aria-label={tBoards('boards.v1.write.card.title')}>
          <div className="bv1-write__head">
            <h2 className="bv1-write__h2">{tBoards('boards.v1.write.card.title')}</h2>
            <span className="bv1-write__sub">{tBoards('boards.v1.write.card.sub', { board: board.name })}</span>
            {!editing && writableBoards.length > 1 && (
              <div className="bv1-write__end">
                <Select
                  value={board.id}
                  ariaLabel={tBoards('boards.v1.write.board_select')}
                  options={writableBoards.map((x) => ({ value: x.id, label: x.name }))}
                  onChange={(id) => { if (id !== board.id) navigate(boardWritePath(id)); }}
                />
              </div>
            )}
          </div>

          {error && <p className="bd-error bv1-write__warn" role="alert">{error}</p>}

          {prefixes.length > 0 && (
            <div className="bv1-fsec">
              <h3 className="bv1-fsec__h3">{tBoards('boards.v1.write.prefix')}</h3>
              <div className="bv1-chips" role="group" aria-label={tBoards('boards.v1.write.prefix')}>
                <FilterChip pressed={form.prefixId === ''} onClick={() => set('prefixId', '')}>
                  {tBoards('boards.editor.prefix_none')}
                </FilterChip>
                {prefixes.map((p) => (
                  <FilterChip key={p.id} pressed={form.prefixId === p.id} onClick={() => set('prefixId', p.id)}>
                    {p.label}
                  </FilterChip>
                ))}
              </div>
            </div>
          )}

          <div className="bv1-fsec">
            <FormField label={tBoards('boards.v1.write.title')} required counter={{ value: form.title.length, max: TITLE_MAX }}>
              <Input
                fullWidth
                maxLength={TITLE_MAX}
                value={form.title}
                aria-label={tBoards('boards.v1.write.title')}
                onChange={(e) => set('title', e.target.value)}
              />
            </FormField>
          </div>

          <div className="bv1-fsec">
            <h3 className="bv1-fsec__h3">{tBoards('boards.v1.write.content')}</h3>
            {/*
              본문 — 리치 에디터 (2026-09-23). 종전 `<textarea>` 를 갈음한다.

              ⛔ **장식 도구줄(.bv1-etool)을 뺐다.** 그것은 「편집기 결정 뒤」까지 «자리만»
                 그려 두려고 aria-hidden 으로 B/I/U·목록·링크·그림·표를 적어 둔 것이었다.
                 이제 진짜 편집기가 붙었는데 **그 단추들은 여전히 안 눌린다** — 실제 편집기
                 옆에 눌리지 않는 단추를 두면 그 글자들이 «거짓»이 된다. 도구모음은 다음 판이다.
                 (붙여넣기로 오는 서식은 «그대로» 살아남는다 — 방향 Ⓐ.)

              ⛔ maxLength 도 없어졌다 — 에디터는 «저장되는 HTML 의 길이»로 재고 90% 에서
                 띠를 띄운다(글자 수로 막으면 표에서 25배 어긋난다). 아래 칸은 그 값을 받아 적고,
                 **못 재면 비운다**(「0자」를 지어내지 않는다).
            */}
            <div className="bv1-editor">
              {/* 2026-10-14 — AxiEditor(툴바·문서 가져오기·그림 편집기). `VITE_BOARD_EDITOR=tiptap` 이면 종전 편집기. */}
              {BOARD_EDITOR === 'axi' ? (
                <LazyAxiBoardEditor
                  key={post?.id ?? 'new'}
                  initialHtml={startForm.content}
                  onChangeHtml={(html) => set('content', html)}
                  contentMaxLength={CONTENT_MAX}
                  boardId={board.id}
                  ariaLabel={tBoards('boards.v1.write.content')}
                />
              ) : (
              <LazyRichTextEditor
                key={post?.id ?? 'new'}
                initialHtml={startForm.content}
                onChangeHtml={(html) => set('content', html)}
                contentMaxLength={CONTENT_MAX}
                boardId={board.id}
                ariaLabel={tBoards('boards.v1.write.content')}
              />
              )}
              {/* 「도구줄은 아직 없습니다」 — tiptap 일 때만 맞는 말이다(AxiEditor 는 도구줄이 있다). */}
              {BOARD_EDITOR === 'tiptap' && (
              <div className="bv1-ecount">
                <span>{tBoards('boards.v1.write.etool.hint')}</span>
              </div>
              )}
            </div>
          </div>

          <div className="bv1-fsec">
            <h3 className="bv1-fsec__h3">{tBoards('boards.v1.write.files')}</h3>
            <div
              className={`bv1-drop${dragOver ? ' is-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <Upload aria-hidden className="bv1-drop__icon" />
              <div className="bv1-drop__text">
                <b>{tBoards('boards.v1.write.drop.title')}</b>
                <span>{tBoards(boardQuotaNoticeKey(board.scopeType))} · {tBoards('boards.v1.write.files.limit', { n: upload.maxItems })}</span>
              </div>
              <Button
                variant="secondary"
                size="compact"
                className="bv1-drop__pick"
                disabled={submitting || !!upload.uploadingFile || atLimit}
                onClick={upload.handleAttachClick}
              >
                {tBoards('boards.v1.write.drop.pick')}
              </Button>
              <input
                ref={upload.fileInputRef}
                type="file"
                multiple
                hidden
                tabIndex={-1}
                aria-hidden="true"
                data-testid="bv1-write-file-input"
                onChange={(e) => void upload.handleFileSelected(e)}
              />
            </div>
            {fileRows.length > 0 && (
              <Table
                caption={tBoards('boards.v1.write.files.caption')}
                columns={fileColumns}
                rows={fileRows}
                rowKey={(r) => r.key}
                wrapClassName="bv1-tablewrap"
              />
            )}
          </div>
        </section>

        <aside className="bv1-card bv1-pubset" aria-label={tBoards('boards.v1.write.pub.title')}>
          <div className="bv1-pubset__head">
            <h2 className="bv1-write__h2">{tBoards('boards.v1.write.pub.title')}</h2>
          </div>

          {board.allowAnonymous && (
            <OptRow title={tBoards('boards.editor.anonymous')} hint={tBoards('boards.v1.write.anonymous_hint')}>
              <Switch checked={form.isAnonymous} onCheckedChange={(v) => set('isAnonymous', v)} aria-label={tBoards('boards.editor.anonymous')} />
            </OptRow>
          )}
          <OptRow
            title={tBoards('boards.editor.secret')}
            hint={tBoards(board.usePostReaders ? 'boards.v1.write.secret_hint_readers' : 'boards.v1.write.secret_hint')}
          >
            <Switch checked={form.isSecret} onCheckedChange={(v) => set('isSecret', v)} aria-label={tBoards('boards.editor.secret')} />
          </OptRow>
          <OptRow title={tBoards('boards.editor.allow_comments')} hint={tBoards('boards.v1.write.comments_hint')}>
            <Switch checked={form.allowComments} onCheckedChange={(v) => set('allowComments', v)} aria-label={tBoards('boards.editor.allow_comments')} />
          </OptRow>
          {canManage && (
            <OptRow title={tBoards('boards.v1.write.pin')} hint={tBoards('boards.v1.write.pin_hint')}>
              <Switch checked={form.pinned} onCheckedChange={(v) => set('pinned', v)} aria-label={tBoards('boards.v1.write.pin')} />
            </OptRow>
          )}

          {!editing && board.notifyEnabled && (
            <>
              <OptRow
                title={tBoards('boards.editor.notify')}
                hint={tBoards(board.notifyAllByDefault ? 'boards.v1.write.notify_default_all' : 'boards.v1.write.notify_default_none')}
              >
                <Switch checked={form.notify} onCheckedChange={(v) => set('notify', v)} aria-label={tBoards('boards.editor.notify')} />
              </OptRow>
              {form.notify && (
                <div className="bv1-tgtbox">
                  <div className="bv1-tgtbox__row">
                    <span>{tBoards(form.notifyTargets.length > 0 ? 'boards.v1.write.notify_box_picked' : 'boards.v1.write.notify_box_default')}</span>
                    <Button variant="secondary" size="sm" onClick={() => setPicker('notify')}>{tBoards('boards.v1.write.pick')}</Button>
                  </div>
                  {form.notifyTargets.length > 0 && (
                    <div className="bv1-tchips"><span className="bv1-tchip">{summarizeTargets(form.notifyTargets)}</span></div>
                  )}
                  <p className="bv1-ntitle">{tBoards('boards.v1.write.notify_later')}</p>
                </div>
              )}
            </>
          )}
          {!editing && !board.notifyEnabled && (
            <>
              <OptRow title={tBoards('boards.editor.notify')} hint={tBoards('boards.v1.write.notify_off_hint')} off>
                <Switch checked={false} disabled onCheckedChange={() => undefined} aria-label={tBoards('boards.editor.notify')} />
              </OptRow>
              <div className="bv1-tgtbox">
                <span>{tBoards('boards.v1.write.notify_off_box')}</span>
                {canManage && (
                  <div className="bv1-tgtbox__row">
                    <Button variant="secondary" size="sm" onClick={() => navigate(boardSettingsPath(board.id, 'notify'))}>
                      {tBoards('boards.v1.write.notify_off_open')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
          {editing && (
            <>
              <OptRow
                title={tBoards('boards.v1.write.edit_notify')}
                hint={tBoards(board.notifyEnabled ? 'boards.v1.write.edit_notify_hint' : 'boards.v1.write.notify_off_hint')}
                off={!board.notifyEnabled}
              />
              {board.notifyEnabled && (
                <div className="bv1-tgtbox">
                  <div className="bv1-tgtbox__row">
                    <span>{tBoards(dirty ? 'boards.v1.write.renotify_dirty' : 'boards.v1.write.renotify_q')}</span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={dirty || renotifying || submitting}
                      onClick={() => void renotify()}
                    >
                      {tBoards('boards.v1.write.renotify')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {board.usePostReaders && (
            <OptRow
              title={tBoards('boards.editor.readers')}
              hint={form.isSecret ? summarizeTargets(form.readers) : tBoards('boards.wide.readers_locked')}
              off={!form.isSecret}
            >
              <Button variant="secondary" size="sm" disabled={!form.isSecret} onClick={() => setPicker('readers')}>
                {tBoards('boards.v1.write.readers_pick')}
              </Button>
            </OptRow>
          )}
          {!board.usePostReaders && editing && startForm.readers.length > 0 && (
            <OptRow
              title={tBoards('boards.editor.readers')}
              hint={tBoards('boards.v1.write.readers_fixed', { n: startForm.readers.length })}
              off
            />
          )}

          <div className="bv1-pubset__bar">
            <span className="bv1-pubset__note">{note}</span>
            <Button variant="secondary" size="sm" onClick={cancel} disabled={submitting}>{tBoards('boards.btn.cancel')}</Button>
            <Button variant="primary" size="sm" onClick={() => void submit()} disabled={!canSubmit}>{submitLabel}</Button>
          </div>
        </aside>
      </div>

      {picker === 'notify' && (
        <BoardTargetModal
          title={tBoards('boards.editor.notify_targets')}
          value={form.notifyTargets}
          onChange={(v) => set('notifyTargets', v)}
          showNotify={false}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'readers' && (
        <BoardTargetModal
          title={tBoards('boards.editor.readers')}
          value={form.readers}
          onChange={(v) => set('readers', v)}
          onClose={() => setPicker(null)}
        />
      )}

      <UnsavedGuard
        when={dirty && leaveTo === null}
        title={tBoards('boards.v1.write.discard.title')}
        description={tBoards('boards.v1.write.discard.desc')}
        stayLabel={tBoards('boards.v1.write.discard.stay')}
        leaveLabel={tBoards('boards.v1.write.discard.leave')}
      />
    </>
  );
}
